import { ActionSettings, ToolbarButton, TableDataRequest } from '@fl/cmsch-fe-library';
import React, { useCallback, useMemo, memo, FC } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Opt } from 'ts-opt';
import { MaleFleckviehAnimalInList } from 'api/gen/MaleFleckviehAnimalInList';
import { useOurTranslation } from 'app/translations';
import { ConnectedTable } from 'utils/tables';
import { matingFleckviehBullsColumns } from './mating-fleckvieh-bulls-columns';
import { useTableSettingsExists } from '../../hooks/use-table-settings-exists';
import { matingAction, matingSelector } from '../../model';
interface Props {
  /** Whether the export action is currently running. */
  exportInProgress: boolean;
  /** Removes one bull from the table. */
  removeBull(animalId: number): void;
  /** Exports the current bull-selector result. */
  exportBullselectorResult(): void;
}
const MatingBullsFleckviehTableBase: FC<Props> = ({
  exportInProgress,
  removeBull,
  exportBullselectorResult
}) => {
  const dispatch = useDispatch();
  const isColumnSettingsEmpty = !useTableSettingsExists('MATING_C', 'BULL');
  const getMatingBulls = useCallback((tableDataRequest: Opt<TableDataRequest<MaleFleckviehAnimalInList>>) => {
    dispatch(matingAction.getFleckviehMatingBulls(tableDataRequest));
  }, [dispatch]);
  const {
    t
  } = useOurTranslation('common');
  const isPageEmpty = useSelector(matingSelector.isFleckviehPageEmpty);
  const actionSettings: ActionSettings<MaleFleckviehAnimalInList> = useMemo(() => ({
    onRowRemove: removeBull
  }), [removeBull]);
  const headerButtons: Array<ToolbarButton> = useMemo(() => [{
    id: 'exportCows',
    title: t('downloadCsv'),
    onClick: exportBullselectorResult,
    disabled: isPageEmpty,
    role: 'export',
    loading: exportInProgress
  }], [exportBullselectorResult, exportInProgress, isPageEmpty, t]);
  return <ConnectedTable tableId="matingFleckviehBulls" columns={isColumnSettingsEmpty ? [] : matingFleckviehBullsColumns} onFetchTableData={getMatingBulls} rowDataPageSelector={matingSelector.maleFleckviehAnimalsPage} actionSettings={actionSettings} enableTableUserSettings withoutVerticalScrollbar paginationPosition="both" lazyLoadingLimit={20} showFulltextFilter headerButtons={headerButtons} data-sentry-element="ConnectedTable" data-sentry-component="MatingBullsFleckviehTableBase" data-sentry-source-file="mating-bulls-fleckvieh-table.tsx" />;
};

/**
 * Memoized Fleckvieh bull table used by the mating workflow.
 *
 * @param props Table actions and loading state.
 * @returns The Fleckvieh bull table.
 *
 * @example
 * <MatingBullsFleckviehTable
 *   exportInProgress={false}
 *   removeBull={handleRemoveBull}
 *   exportBullselectorResult={handleExport}
 * />
 */
export const MatingBullsFleckviehTable = memo(MatingBullsFleckviehTableBase);